<!-- 
 * @Author       : pan
 * @Date         : 2025-07-11 10:00:00
 * @LastEditors  : pan
 * @LastEditTime : 2025-07-11 10:00:00
 * @FilePath     : app/javascript/dashboard/routes/dashboard/settings/blacklist/components/AddIpRange.vue
 * @Description  : 添加/编辑 IP 黑名单组件
 * @Description  : 删除计算 IP范围的差值方法，使用后端 update 方法中高效处理
 * Copyright 2025 Bruce, All Rights Reserved.
 * 2025-07-11 10:00:00
-->
<script>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import Multiselect from 'vue-multiselect';
import { addMinutes, addHours, addDays } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { compareIpBlacklist, ipToLong } from './BlacklistUtils';
import Modal from 'dashboard/components/Modal.vue';

// 时间范围选项 - 导出以便其他组件使用
export const timeRangeOptions = () => {
  const { t } = useI18n();
  return [
    {
      id: 'permanent',
      name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.PERMANENT'),
    },
    { id: '30m', name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.30M') },
    { id: '1h', name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.1H') },
    { id: '2h', name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.2H') },
    { id: '6h', name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.6H') },
    { id: '24h', name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.24H') },
    { id: '7d', name: t('BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.7D') },
  ];
};

const timeOptionToTimeSec = timeOption => {
  switch (timeOption) {
    case 'permanent':
      return 100 * 12 * 30 * 24 * 60 * 60; // 永久拦截设置一个更大的值 100 年
    case '30m':
      return 30 * 60;
    case '1h':
      return 60 * 60;
    case '2h':
      return 2 * 60 * 60;
    case '6h':
      return 6 * 60 * 60;
    case '24h':
      return 24 * 60 * 60;
    case '7d':
      return 7 * 24 * 60 * 60;
    default:
      return 100 * 12 * 30 * 24 * 60 * 60;
  }
};

/**
 * 将拦截时间选项转换为 yyyy-MM-dd HH:mm:ss 格式的日期字符串
 * @param {string} timeOption - 时间选项ID
 * @returns {string|null} - 格式化的日期字符串，如果是永久拦截则返回null
 */
export const timeOptionToTimestamp = timeOption => {
  const now = new Date();
  let targetDate;

  switch (timeOption) {
    case 'permanent':
      return null; // 永久拦截不设置过期时间
    case '30m':
      targetDate = addMinutes(now, 30);
      break;
    case '1h':
      targetDate = addHours(now, 1);
      break;
    case '2h':
      targetDate = addHours(now, 2);
      break;
    case '6h':
      targetDate = addHours(now, 6);
      break;
    case '24h':
      targetDate = addDays(now, 1);
      break;
    case '7d':
      targetDate = addDays(now, 7);
      break;
    default:
      return null;
  }

  // 格式化日期为 yyyy-MM-dd HH:mm:ss
  return formatDateToString(targetDate);
};

/**
 * 将日期格式化为 yyyy-MM-dd HH:mm:ss 格式
 * @param {Date} date - 日期对象
 * @returns {string} - 格式化的日期字符串
 */
const formatDateToString = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * 从note字段获取拦截时间选项ID
 * @param {string} note - 记录中的note字段
 * @returns {string} - 时间选项ID
 */
export const getNoteTimeOption = note => {
  // 获取所有有效选项ID
  const validOptions = ['permanent', '30m', '1h', '2h', '6h', '24h', '7d'];

  // 如果note是有效的时间选项ID，直接返回
  if (note && validOptions.includes(note)) {
    return note;
  }

  // 默认返回永久拦截
  return 'permanent';
};

/**
 * 获取拦截时间选项的显示名称
 * @param {string} timeOptionId - 时间选项ID
 * @returns {string} - 显示名称
 */
export const getTimeOptionName = timeOptionId => {
  try {
    const { t } = useI18n();
    const key = timeOptionId.toUpperCase();
    return t(`BLACKLIST.IP_BLACKLIST.TIME_OPTIONS.${key}`);
  } catch (error) {
    // 如果 useI18n 在组件外部调用失败，返回一个占位符
    return '---';
  }
};

export default {
  components: {
    Multiselect,
    Modal,
  },
  props: {
    editingData: {
      type: Object,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'refresh'],
  mounted() {
    this.setDropdownWidth();
    window.addEventListener('resize', this.setDropdownWidth);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setDropdownWidth);
  },
  setup(props, { emit }) {
    const store = useStore();
    const { t } = useI18n();
    const ipInputMode = ref('ip-range');
    const ipRangeStart = ref(['', '', '', '']);
    const ipRangeEnd = ref(['', '', '', '']);
    // 标记哪个 IP 段先输入
    const startIpFirst = ref(true);
    const selectedTimeRange = ref(null);
    // 是否显示确认弹窗
    const showConfirmModal = ref(false);
    // 待确认更新数据
    const pendingUpdateData = ref(null);
    // loading
    const isBlacklistLoading = ref(false);

    // 如果是编辑模式，初始化表单数据
    const initFormData = () => {
      if (props.isEditMode && props.editingData) {
        // 解析 IP 范围
        const startIp = props.editingData.blocked_ip_start;
        const endIp = props.editingData.blocked_ip_end;

        // 设置起始 IP
        if (startIp) {
          const startSegments = startIp.split('.');
          startSegments.forEach((segment, index) => {
            if (index < 4) ipRangeStart.value[index] = segment;
          });
        }

        // 设置结束 IP
        if (endIp) {
          const endSegments = endIp.split('.');
          endSegments.forEach((segment, index) => {
            if (index < 4) ipRangeEnd.value[index] = segment;
          });
        }

        // 设置拦截时间 - 从note获取
        if (props.editingData.note) {
          // 从note获取时间选项
          const timeOption = getNoteTimeOption(props.editingData.note);
          const options = timeRangeOptions();
          selectedTimeRange.value = options.find(opt => opt.id === timeOption);
        }
      }
    };

    // 监听编辑模式变化
    watch(
      () => props.isEditMode,
      newValue => {
        if (newValue) {
          initFormData();
        }
      },
      { immediate: true }
    );

    // 处理IP输入
    const handleIpInput = (event, index, type) => {
      const targetArray =
        type === 'start' ? ipRangeStart.value : ipRangeEnd.value;
      const otherArray =
        type === 'start' ? ipRangeEnd.value : ipRangeStart.value;
      const value = event.target.value.replace(/\D/g, '');

      if (value === '') {
        targetArray[index] = '';
        return;
      }

      const numValue = parseInt(value, 10);
      if (numValue > 255) {
        targetArray[index] = '255';
      } else {
        targetArray[index] = numValue.toString();
      }

      // 设置哪个 IP 段先输入
      if (type === 'start') {
        startIpFirst.value = true;
      } else {
        startIpFirst.value = false;
      }

      // 同步前三项
      if (index < 3) {
        // 检查前三项是否都有值
        const firstThreeComplete = targetArray
          .slice(0, 3)
          .every(segment => segment !== '');
        if (firstThreeComplete) {
          // 同步前三项到另一个 IP 段
          for (let i = 0; i < 3; i++) {
            otherArray[i] = targetArray[i];
          }
        }
      }

      // 自动跳转到下一个输入框
      if (value.length === 3 && index < 3) {
        const nextInput = event.target
          .closest('.ip-input-group')
          .querySelector(`input[data-index="${index + 1}"]`);
        if (nextInput) nextInput.focus();
      }
    };

    // 处理退格键事件
    const handleBackspace = (event, index, type) => {
      const targetArray =
        type === 'start' ? ipRangeStart.value : ipRangeEnd.value;

      if (!targetArray[index] && index > 0 && event.key === 'Backspace') {
        const prevInput = event.target
          .closest('.ip-input-group')
          .querySelector(`input[data-index="${index - 1}"]`);
        if (prevInput) {
          prevInput.focus();
          prevInput.setSelectionRange(
            prevInput.value.length,
            prevInput.value.length
          );
        }
      }
    };

    // 处理粘贴事件
    const handlePaste = (event, type) => {
      event.preventDefault();
      const targetArray =
        type === 'start' ? ipRangeStart.value : ipRangeEnd.value;
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        'text'
      );
      const ipParts = pastedText.split('.');

      if (ipParts.length === 4) {
        ipParts.forEach((part, index) => {
          const value = part.replace(/\D/g, '');
          targetArray[index] = parseInt(value) > 255 ? '255' : value;
        });
        // 同步前三项到另一个 IP 段
        const otherArray =
          type === 'start' ? ipRangeEnd.value : ipRangeStart.value;
        for (let i = 0; i < 3; i++) {
          otherArray[i] = targetArray[i];
        }
      }
    };

    // 验证IP段输入
    const validateIpRange = () => {
      // 检查是否所有字段都已填写
      const isStartIpComplete = ipRangeStart.value.every(
        segment => segment !== ''
      );
      const isEndIpComplete = ipRangeEnd.value.every(segment => segment !== '');

      if (!isStartIpComplete || !isEndIpComplete) {
        // 可以添加错误提示
        return false;
      }

      if (!selectedTimeRange.value) {
        // 可以添加错误提示
        return false;
      }

      // 验证IP范围的有效性
      const startIp = ipRangeStart.value.join('.');
      const endIp = ipRangeEnd.value.join('.');
      const startIpNum = ipToLong(startIp);
      const endIpNum = ipToLong(endIp);

      if (startIpNum > endIpNum) {
        // 起始IP应小于或等于结束IP
        useAlert(t('BLACKLIST.START_IP_LESS_OR_END_IP'));
        return false;
      }

      return true;
    };

    // 表单验证计算属性
    const isFormValid = computed(() => {
      // 检查IP段是否完整填写
      const isStartIpComplete = ipRangeStart.value.every(
        segment => segment !== ''
      );
      const isEndIpComplete = ipRangeEnd.value.every(segment => segment !== '');

      // 检查是否选择了拦截时间
      const isTimeRangeSelected = !!selectedTimeRange.value;

      return isStartIpComplete && isEndIpComplete && isTimeRangeSelected;
    });

    // 重置IP段表单
    const resetIpRangeForm = () => {
      ipRangeStart.value = ['', '', '', ''];
      ipRangeEnd.value = ['', '', '', ''];
      selectedTimeRange.value = null;
    };

    // 处理添加或更新IP段
    const handleAddIpRange = async () => {
      // 验证IP段输入
      if (!validateIpRange()) {
        return;
      }
      // 构建IP段范围
      const startIp = ipRangeStart.value.join('.');
      const endIp = ipRangeEnd.value.join('.');

      // 获取拦截时间和note
      const noteValue = selectedTimeRange.value.id; // 将选项ID保存到note字段

      // 构建请求数据
      const requestData = {
        blocked_ip_start: startIp,
        blocked_ip_end: endIp,
        expiration_time_type: timeOptionToTimeSec(noteValue),
        note: noteValue, // 添加note字段
      };

      if (props.isEditMode && props.editingData) {
        const { hasChanged } = compareIpBlacklist(
          props.editingData,
          requestData
        );
        if (!hasChanged) {
          // 没有变化，展示确认弹窗确认
          showConfirmModal.value = true; // Show the modal
          pendingUpdateData.value = requestData; // Save the pending update data
          return;
        }
      }

      isBlacklistLoading.value = true;
      try {
        if (props.isEditMode && props.editingData) {
          // 编辑模式：更新现有IP段
          await store.dispatch('blacklist/updateIp', {
            id: props.editingData.id,
            data: requestData,
          });

          // 编辑成功提示
          useAlert(t('BLACKLIST.ALERTS.IP_UPDATE_SUCCESS'));
        } else {
          // 添加模式：添加新IP段
          await store.dispatch('blacklist/createIp', requestData);

          // 添加成功提示
          useAlert(t('BLACKLIST.ALERTS.IP_ADD_SUCCESS'));
        }

        // 重置表单
        resetIpRangeForm();

        // 关闭弹窗
        emit('close');

        // 通知父组件刷新数据
        emit('refresh');
      } catch (error) {
        // 根据操作类型显示不同的错误提示
        useAlert(
          props.isEditMode
            ? t('BLACKLIST.ALERTS.IP_UPDATE_FAILURE')
            : t('BLACKLIST.ALERTS.IP_ADD_FAILURE')
        );
      } finally {
        isBlacklistLoading.value = false;
      }
    };

    // 确认更新
    const confirmUpdate = async () => {
      isBlacklistLoading.value = true;
      try {
        await store.dispatch('blacklist/updateIp', {
          id: props.editingData.id,
          data: pendingUpdateData.value,
        });

        // 更新成功提示
        useAlert(t('BLACKLIST.ALERTS.IP_UPDATE_SUCCESS'));
        emit('refresh');
        cancelUpdate();
      } catch (error) {
        showConfirmModal.value = false;
        useAlert(t('BLACKLIST.ALERTS.IP_UPDATE_FAILURE'));
      } finally {
        isBlacklistLoading.value = false;
      }
    };
    // 取消更新
    const cancelUpdate = () => {
      showConfirmModal.value = false; // Close the modal
      emit('close');
    };

    // 下拉框宽度处理
    const timeRangeSelect = ref(null);

    // 设置下拉框宽度
    const setDropdownWidth = () => {
      if (timeRangeSelect.value) {
        const selectWidth =
          timeRangeSelect.value.querySelector('.multiselect').offsetWidth;
        document.documentElement.style.setProperty(
          '--ms-width',
          `${selectWidth}px`
        );
      }
    };

    return {
      ipInputMode,
      ipRangeStart,
      ipRangeEnd,
      selectedTimeRange,
      timeRangeOptions,
      timeRangeSelect,
      setDropdownWidth,
      startIpFirst,
      handleIpInput,
      handleBackspace,
      handlePaste,
      handleAddIpRange,
      isFormValid,
      onClose: () => emit('close'),
      showConfirmModal,
      confirmUpdate,
      cancelUpdate,
      isBlacklistLoading,
    };
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <form
      v-if="!showConfirmModal"
      class="flex flex-wrap mx-0"
      @submit.prevent="handleAddIpRange"
    >
      <div class="px-6 py-4 w-full">
        <h3 class="text-lg font-medium text-slate-800 mb-4">
          {{
            isEditMode
              ? $t('BLACKLIST.IP_BLACKLIST.EDIT_IP_RANGE')
              : $t('BLACKLIST.IP_BLACKLIST.ADD_IP_RANGE')
          }}
        </h3>

        <!-- IP段输入 -->
        <div class="mb-5">
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ $t('BLACKLIST.IP_BLACKLIST.IP_SEGMENT_RANGE') }}
          </label>
          <div class="flex items-center">
            <!-- 起始IP -->
            <div
              class="flex h-10 items-center border border-slate-300 rounded-md bg-white dark:bg-slate-900 hover:border-indigo-500 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 px-2 ip-input-group"
            >
              <template v-for="(segment, index) in ipRangeStart" :key="index">
                <input
                  v-model="ipRangeStart[index]"
                  type="text"
                  maxlength="3"
                  class="reset-base w-10 min-w-0 text-center bg-transparent text-sm text-slate-900 dark:text-slate-75"
                  style="
                    -webkit-appearance: none;
                    appearance: none;
                    border: none;
                    outline: none;
                  "
                  @input="e => handleIpInput(e, index, 'start')"
                  @keydown.backspace="e => handleBackspace(e, index, 'start')"
                  @paste="e => handlePaste(e, 'start')"
                  :data-index="index"
                  :disabled="
                    !startIpFirst &&
                    index < 3 &&
                    ipRangeEnd.slice(0, 3).every(segment => segment !== '')
                  "
                />
                <span
                  v-if="index < 3"
                  class="text-slate-600 mx-1 select-none shrink-0"
                  >.</span
                >
              </template>
            </div>

            <!-- 分隔符 -->
            <div class="mx-3 text-slate-500 font-medium">-</div>

            <!-- 结束IP -->
            <div
              class="flex h-10 items-center border border-slate-300 rounded-md bg-white dark:bg-slate-900 hover:border-indigo-500 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 px-2 ip-input-group"
            >
              <template v-for="(segment, index) in ipRangeEnd" :key="index">
                <input
                  v-model="ipRangeEnd[index]"
                  type="text"
                  maxlength="3"
                  class="reset-base w-10 min-w-0 text-center bg-transparent text-sm text-slate-900 dark:text-slate-75"
                  style="
                    -webkit-appearance: none;
                    appearance: none;
                    border: none;
                    outline: none;
                  "
                  @input="e => handleIpInput(e, index, 'end')"
                  @keydown.backspace="e => handleBackspace(e, index, 'end')"
                  @paste="e => handlePaste(e, 'end')"
                  :data-index="index"
                  :disabled="
                    startIpFirst &&
                    index < 3 &&
                    ipRangeStart.slice(0, 3).every(segment => segment !== '')
                  "
                />
                <span
                  v-if="index < 3"
                  class="text-slate-600 mx-1 select-none shrink-0"
                  >.</span
                >
              </template>
            </div>
          </div>
        </div>

        <!-- 时间段选择 -->
        <div class="mb-5">
          <label class="block text-sm font-medium text-slate-700 mb-1">
            {{ $t('BLACKLIST.IP_BLACKLIST.BLOCK_TIME') }}
          </label>
          <div class="multiselect-wrap--small relative" ref="timeRangeSelect">
            <multiselect
              v-model="selectedTimeRange"
              track-by="id"
              label="name"
              :placeholder="$t('BLACKLIST.IP_BLACKLIST.SELECT_BLOCK_TIME')"
              selected-label=""
              :select-label="$t('BLACKLIST.IP_BLACKLIST.ENTER_TO_SELECTTER')"
              deselect-label=""
              :options="timeRangeOptions()"
              :searchable="false"
              :allow-empty="false"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center justify-end w-full gap-3 px-6 py-4">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-75"
          @click="onClose"
        >
          {{ $t('BLACKLIST.CANCEL') }}
        </button>
        <woot-submit-button
          type="submit"
          :disabled="!isFormValid"
          :loading="isBlacklistLoading"
          :button-text="
            isEditMode ? $t('BLACKLIST.SAVE') : $t('BLACKLIST.CONFIRM')
          "
        >
        </woot-submit-button>
      </div>
    </form>
    <div v-else class="p-6">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        {{ $t('BLACKLIST.CONFIRM_UPDATE') }}
      </h3>
      <p class="text-sm text-gray-500 mb-6">
        {{ $t('BLACKLIST.CONFIRM_UPDATE_INFO') }}
      </p>
      <div class="flex justify-end space-x-3">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-75"
          @click="cancelUpdate"
        >
          {{ $t('BLACKLIST.CANCEL') }}
        </button>
        <woot-submit-button
          @click="confirmUpdate"
          :button-text="$t('BLACKLIST.CONFIRM')"
          :loading="isBlacklistLoading"
        >
        </woot-submit-button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.multiselect) {
  min-height: 38px;
}

:deep(.multiselect__tags) {
  min-height: 38px;
  padding: 8px 40px 0 8px;
  border-radius: 4px;
  border-color: #cbd5e1;
}

:deep(.multiselect__select) {
  height: 38px;
}

:deep(.multiselect__single) {
  margin-bottom: 0;
  padding-left: 4px;
  font-size: 14px;
}

:deep(.multiselect__content-wrapper) {
  position: fixed;
  width: var(--ms-width);
  max-height: 300px !important;
  margin: 0;
  z-index: 999;
}

:deep(.multiselect__option--highlight) {
  background-color: var(--woot-color);
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
  background-color: var(--r-400);
}
</style>
